import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { Skeleton } from 'antd'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { parseAndDownloadCSV, GrPrice, orderedExportData } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import { API } from '@getgreenline/homi-shared'
import Helmet from 'react-helmet'
import {
  ReportFields,
  IReportV2Query,
  TimePeriod,
  IBaseReportData,
} from '@getgreenline/homi-shared/dist/models/Reports/ReportsV2'
import { groupBy } from 'lodash'
import { Track } from '../../../../../Track'
import { InsightsRoutes, Routes } from '../../../../../constants/Routes'
import { ReportAlert } from '../ReportAlert'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface State {
  groupBy: TimePeriod
  data?: IBaseReportData[]
  isLoading: boolean
}

@inject('reportFilterStore')
@observer
export class FinancialReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      groupBy: 'day',
      data: undefined,
      isLoading: false,
    }
  }

  componentDidMount = async () => {
    await this.getResults()
  }

  reloadData = async () => {
    await this.getResults()
  }

  get query(): IReportV2Query {
    const { selectedLocationIds, startDateString, endDateString } = this.props.reportFilterStore!
    return {
      locationIds: selectedLocationIds,
      startDate: startDateString,
      endDate: endDateString,
      orderBy: {
        direction: 'asc',
        field: 'Date',
      },
      groupBy: {
        time: this.state.groupBy,
        dimensions: ['Location'],
      },
      fields: this.fields,
    }
  }

  get fields() {
    const fields: ReportFields[] = [
      'Location',
      'Date',
      'Gross sales',
      'Refunds',
      'Discounts',
      'Net sales',
      'Taxes',
      'Deposits',
      'Gift cards',
      'Penny adjustment',
      'Online order discrepancies',
      'Total',
      'Cost',
      'Profit',
      'Gift card discounts',
      'Gift card refunds',
    ]
    return fields
  }

  get taxHeaders(): string[] {
    if (!this.state.data) {
      return []
    }
    const headers: string[] = []
    if (this.state.data) {
      this.state.data.forEach((row) => {
        const taxes = row['Taxes']
        if (taxes) {
          taxes.forEach((tax) => {
            const taxName = tax['Tax']
            if (!headers.includes(taxName) && taxName !== null) {
              headers.push(taxName)
            }
          })
        }
      })
    }

    return headers
  }

  getTaxes = (data: IBaseReportData) => {
    const groupedTaxes = groupBy(data['Taxes'], 'Tax')
    const taxes = this.taxHeaders.map((header: string) => {
      if (groupedTaxes[header]) {
        return (
          <td key={header} className='text-right'>
            {GrPrice.convertDollarToDollar(groupedTaxes[header][0]['Amount'])}
          </td>
        )
      } else {
        return (
          <td key={header} className='text-right'>
            {GrPrice.convertDollarToDollar(0)}
          </td>
        )
      }
    })
    return taxes
  }

  getResults = async () => {
    const { companyId } = this.props
    await this.setState({ data: [], isLoading: true })
    const { startDateString, endDateString } = this.props.reportFilterStore!

    try {
      Track.startTimer('dashboard:reports_financials')
      const data = await API.getSales(companyId, this.query)
      Track.track('dashboard:reports_financials', {
        numberOfDays: moment(endDateString).diff(startDateString, 'days'),
      })

      this.setState({ data, isLoading: false })
    } catch (error) {
      this.setState({ data: undefined, isLoading: false })
    }
  }

  tableRenderer = (rawData?: IBaseReportData[]) => {
    if (!rawData) {
      return <div>Error</div>
    }

    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>Location</th>
              <th>Date</th>
              <th className='text-right'>Gross sales</th>
              <th className='text-right'>Refunds</th>
              <th className='text-right'>Discounts</th>
              <th className='text-right'>Net sales</th>
              {this.taxHeaders.map((taxName: string, index: number) => {
                return (
                  <th key={index} className='text-right'>
                    {taxName}
                  </th>
                )
              })}
              <th className='text-right'>Deposits</th>
              <th className='text-right'>Penny adjustment</th>
              <th className='text-right'>Online order discrepancies</th>
              <th className='text-right'>Gift card sales</th>
              <th className='text-right'>Gift card refunds</th>
              <th className='text-right'>Gift card discounts</th>
              <th className='text-right'>Total</th>
              <th className='text-right'>Cost</th>
              <th className='text-right'>Profit</th>
            </tr>
          </thead>
          <tbody>
            {rawData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>{data['Location']}</td>
                  <td>{data['Date'].substring(0, 10)}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Gross sales'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Refunds'] || 0)}
                  </td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Discounts'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Net sales'])}</td>
                  {this.getTaxes(data)}
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Deposits'])}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Penny adjustment'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Online order discrepancies'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Gift cards'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Gift card refunds'])}
                  </td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['Gift card discounts'])}
                  </td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Total'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Cost'])}</td>
                  <td className='text-right'>{GrPrice.convertDollarToDollar(data['Profit'])}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  groupDataBy = (groupBy: TimePeriod) => {
    this.setState(
      {
        groupBy: groupBy,
      },
      this.reloadData,
    )
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = orderedExportData(this.fields, this.state.data, this.taxHeaders)
    if (data) {
      parseAndDownloadCSV(`financials-${startDate}-${endDate}`, data)
    }
  }

  get inSightReportPath(): string {
    return Routes.INSIGHTS_REPORTS(this.props.companyId, InsightsRoutes.SALES_SUMMARY)
  }

  render() {
    const { isLoading } = this.state

    return (
      <>
        <Helmet>
          <title>Reports | Financial</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <ReportFilter maxRangeMonths={12} reportPath={this.inSightReportPath} />

        <ReportAlert reportType='insights' reportPath={this.inSightReportPath} />

        <div className='rounded background-white'>
          <div className='p-3'>
            <div>
              <b>Financials</b>
            </div>

            <div className='mt-2 mb-2'>
              <UncontrolledButtonDropdown className='mr-2'>
                <DropdownToggle caret size='sm' color='light' style={{ textAlign: 'left' }}>
                  Group by {this.state.groupBy}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => this.groupDataBy('day')}>day</DropdownItem>
                  <DropdownItem onClick={() => this.groupDataBy('week')}>week</DropdownItem>
                  <DropdownItem onClick={() => this.groupDataBy('month')}>month</DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
              <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                Export as csv
              </button>
            </div>

            <div className='table-responsive'>
              {isLoading ? <Skeleton active /> : this.tableRenderer(this.state.data)}
            </div>
          </div>
        </div>
      </>
    )
  }
}
